import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { likePost, unlikePost, removePost, reportPost, updatePost, deletePost } from '../store/slices/postsSlice';
import { uploadAPI } from '../services/api';
import './PostCard.css';

interface User {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string;
  trust_rating: number;
  is_admin?: boolean;
}

interface MediaItem {
  id: string;
  media_type: 'image' | 'video';
  url: string;
  thumbnail_url?: string;
  original_url?: string;
  position: number;
  width?: number;
  height?: number;
  duration_seconds?: number;
}

interface PostCardProps {
  post: {
    id: string;
    user_id: string;
    content: string;
    image_url: string;
    location_name: string;
    is_verified_location: boolean;
    like_count: number;
    comment_count: number;
    edit_count: number;
    is_liked: boolean;
    created_at: string;
    user: User;
    media?: MediaItem[];
    distance_meters?: number;
    city?: string;
    state_province?: string;
    country?: string;
  };
}

interface FullscreenMedia {
  url: string;
  type: 'image' | 'video';
}

const isPortrait = (item: MediaItem) => {
  if (item.width && item.height) {
    return item.height > item.width;
  }
  return false;
};

const PostCard: React.FC<PostCardProps> = ({ post }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [reportingPost, setReportingPost] = useState(false);
  const [reportReason, setReportReason] = useState('spam');
  const [reported, setReported] = useState(false);
  const [fullscreenMedia, setFullscreenMedia] = useState<FullscreenMedia | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content || '');
  const [editMedia, setEditMedia] = useState<MediaItem[]>(post.media || []);
  const [removeMediaIDs, setRemoveMediaIDs] = useState<string[]>([]);
  const [addMediaItems, setAddMediaItems] = useState<any[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isOwner = user?.id === post.user_id;

  const handleDelete = async () => {
    try {
      await dispatch(deletePost(post.id)).unwrap();
    } catch {
      // silently fail
    }
    setConfirmingDelete(false);
  };

  const startEditing = () => {
    setEditContent(post.content || '');
    setEditMedia(post.media || []);
    setRemoveMediaIDs([]);
    setAddMediaItems([]);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setRemoveMediaIDs([]);
    setAddMediaItems([]);
  };

  const handleRemoveEditMedia = (mediaId: string) => {
    setRemoveMediaIDs((prev) => [...prev, mediaId]);
    setEditMedia((prev) => prev.filter((m) => m.id !== mediaId));
  };

  const handleAddEditMedia = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadAPI.uploadMedia(file);
      const uploaded = res.data;
      setAddMediaItems((prev) => [...prev, {
        url: uploaded.url,
        original_url: uploaded.original_url,
        thumbnail_url: uploaded.thumbnail_url,
        media_type: uploaded.media_type,
        width: uploaded.width,
        height: uploaded.height,
        duration_seconds: uploaded.duration_seconds,
        file_size_bytes: uploaded.file_size,
      }]);
      setEditMedia((prev) => [...prev, {
        id: `new-${Date.now()}`,
        media_type: uploaded.media_type as 'image' | 'video',
        url: uploaded.url,
        thumbnail_url: uploaded.thumbnail_url,
        original_url: uploaded.original_url,
        position: prev.length,
        width: uploaded.width,
        height: uploaded.height,
      }]);
    } catch {
      // silently fail
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSaveEdit = async () => {
    setEditSaving(true);
    try {
      await dispatch(updatePost({
        postId: post.id,
        data: {
          content: editContent,
          remove_media_ids: removeMediaIDs.length > 0 ? removeMediaIDs : undefined,
          add_media_items: addMediaItems.length > 0 ? addMediaItems : undefined,
        },
      })).unwrap();
      setEditing(false);
    } catch {
      // silently fail
    }
    setEditSaving(false);
  };

  const handleReport = async () => {
    try {
      await dispatch(reportPost({ postId: post.id, reason: reportReason })).unwrap();
      setReported(true);
    } catch {
      // silently fail
    }
    setReportingPost(false);
  };

  const handleLike = () => {
    if (post.is_liked) {
      dispatch(unlikePost(post.id));
    } else {
      dispatch(likePost(post.id));
    }
  };

  const formatDistance = (meters?: number) => {
    if (!meters) return null;
    if (meters < 1000) return `${Math.round(meters)}m away`;
    return `${(meters / 1000).toFixed(1)}km away`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h`;
    if (hours < 168) return `${Math.floor(hours / 24)}d`;
    return date.toLocaleDateString();
  };

  const getTrustClass = (rating: number) => {
    if (rating >= 7) return 'trust-high';
    if (rating >= 4) return 'trust-medium';
    return 'trust-low';
  };

  const hasMedia = post.media && post.media.length > 0;

  const renderMedia = () => {
    if (hasMedia) {
      const items = post.media!;
      const count = items.length;

      if (count === 1) {
        const item = items[0];
        const portrait = isPortrait(item);
        return item.media_type === 'video' ? (
          <div
            className="video-thumbnail-wrapper carousel-single"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setFullscreenMedia({ url: item.url, type: 'video' });
            }}
          >
            {item.thumbnail_url ? (
              <img src={item.thumbnail_url} alt="Video thumbnail" className="post-media-image" />
            ) : (
              <div className="video-placeholder" />
            )}
            <div className="video-play-overlay">
              <span className="video-play-icon">▶</span>
            </div>
          </div>
        ) : (
          <img
            src={item.url}
            alt="Post media"
            className={`${portrait ? 'post-image-portrait' : 'post-image-square'} post-image-clickable`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setFullscreenMedia({ url: item.original_url || item.url, type: 'image' });
            }}
          />
        );
      }

      // Carousel for multiple items
      const currentItem = items[carouselIndex];
      const portrait = isPortrait(currentItem);
      return (
        <div className="media-carousel">
          <div className="carousel-slide">
            {currentItem.media_type === 'video' ? (
              <div
                className="video-thumbnail-wrapper"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setFullscreenMedia({ url: currentItem.url, type: 'video' });
                }}
              >
                {currentItem.thumbnail_url ? (
                  <img src={currentItem.thumbnail_url} alt="Video thumbnail" className="carousel-image" />
                ) : (
                  <div className="video-placeholder" />
                )}
                <div className="video-play-overlay">
                  <span className="video-play-icon">▶</span>
                </div>
              </div>
            ) : (
              <img
                src={currentItem.url}
                alt="Post media"
                className={portrait ? 'carousel-image-portrait' : 'carousel-image'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setFullscreenMedia({ url: currentItem.original_url || currentItem.url, type: 'image' });
                }}
              />
            )}
            {carouselIndex > 0 && (
              <button className="carousel-arrow carousel-arrow-left" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCarouselIndex(carouselIndex - 1); }}>
                ‹
              </button>
            )}
            {carouselIndex < count - 1 && (
              <button className="carousel-arrow carousel-arrow-right" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCarouselIndex(carouselIndex + 1); }}>
                ›
              </button>
            )}
          </div>
          <div className="carousel-dots">
            {items.map((_, i) => (
              <span
                key={i}
                className={`carousel-dot ${i === carouselIndex ? 'active' : ''}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCarouselIndex(i); }}
              />
            ))}
          </div>
        </div>
      );
    }

    // Legacy single image fallback (no dimensions, default to square)
    if (post.image_url) {
      return (
        <img
          src={post.image_url}
          alt="Post"
          className="post-image-square post-image-clickable"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setFullscreenMedia({ url: post.image_url, type: 'image' });
          }}
        />
      );
    }

    return null;
  };

  return (
    <>
      <article className="post-card">
        <div className="post-card-header">
          <Link to={`/profile/${post.user.id}`} className="post-avatar">
            <img
              src={post.user.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.user.username}`}
              alt={post.user.display_name}
              className="avatar"
            />
          </Link>
          <div className="post-header-line1">
            <Link to={`/profile/${post.user.id}`} className="post-user">
              <span className="display-name">{post.user.display_name || post.user.username}</span>
              <span className="username">@{post.user.username}</span>
              {post.user.is_admin && <span className="admin-badge">Admin</span>}
            </Link>
            <span className={`trust-badge ${getTrustClass(post.user.trust_rating)}`}>
              ★ {post.user.trust_rating}
            </span>
            <span className="post-time">{formatDate(post.created_at)}</span>
            {post.edit_count > 0 && <span className="edited-badge">(edited)</span>}
          </div>
        </div>

        {editing ? (
          <div className="edit-mode">
            <textarea
              className="edit-textarea"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              maxLength={500}
              rows={3}
            />
            {editMedia.length > 0 && (
              <div className="edit-media-grid">
                {editMedia.map((m) => (
                  <div key={m.id} className="edit-media-item">
                    <img src={m.thumbnail_url || m.url} alt="Media" />
                    <button className="edit-media-remove" onClick={() => handleRemoveEditMedia(m.id)}>✕</button>
                  </div>
                ))}
              </div>
            )}
            {editMedia.length < 10 && (
              <>
                <input
                  type="file"
                  accept="image/*,video/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleAddEditMedia}
                />
                <button className="edit-add-media-btn" onClick={() => fileInputRef.current?.click()}>
                  + Add media
                </button>
              </>
            )}
            <div className="edit-actions">
              <button className="edit-save-btn" onClick={handleSaveEdit} disabled={editSaving}>
                {editSaving ? 'Saving...' : 'Save'}
              </button>
              <button className="edit-cancel-btn" onClick={cancelEditing}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <Link to={`/post/${post.id}`} className="post-body">
              <p className="post-text">{post.content}</p>
            </Link>

            {renderMedia()}
          </>
        )}

        <div className="post-location">
          <span className={`location-badge ${post.is_verified_location ? 'verified-location' : ''}`}>
            📍 {[post.city, post.state_province, post.country].filter(Boolean).join(', ') || 'Unknown location'}
            {post.is_verified_location && ' ✓'}
          </span>
          {post.distance_meters && (
            <span className="distance">{formatDistance(post.distance_meters)}</span>
          )}
        </div>

        <div className="post-actions">
          <button
            className={`action-btn ${post.is_liked ? 'liked' : ''}`}
            onClick={handleLike}
          >
            {post.is_liked ? '❤️' : '🤍'} {post.like_count}
          </button>
          <Link to={`/post/${post.id}`} className="action-btn">
            💬 {post.comment_count}
          </Link>
          <button className="action-btn">
            🔄
          </button>
          <button className="action-btn">
            📤
          </button>
          {user && user.id !== post.user.id && (
            reported ? (
              <span className="report-done">🚩</span>
            ) : reportingPost ? (
              <span className="report-confirm">
                <select value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
                  <option value="spam">Spam</option>
                  <option value="harassment">Harassment</option>
                  <option value="misinformation">Misinformation</option>
                  <option value="inappropriate">Inappropriate</option>
                  <option value="other">Other</option>
                </select>
                <button className="action-btn delete-yes" onClick={handleReport}>Submit</button>
                <button className="action-btn" onClick={() => setReportingPost(false)}>Cancel</button>
              </span>
            ) : (
              <button className="action-btn" onClick={() => setReportingPost(true)}>
                🚩
              </button>
            )
          )}
          {isOwner && !editing && (
            <button className="action-btn" onClick={startEditing}>
              ✏️
            </button>
          )}
          {(isOwner || user?.is_admin) && (
            confirmingDelete ? (
              <span className="delete-confirm">
                <button className="action-btn delete-yes" onClick={handleDelete}>Confirm</button>
                <button className="action-btn" onClick={() => setConfirmingDelete(false)}>Cancel</button>
              </span>
            ) : (
              <button className="action-btn delete-action" onClick={() => setConfirmingDelete(true)}>
                🗑️
              </button>
            )
          )}
        </div>
      </article>

      {fullscreenMedia && (
        <div className="fullscreen-overlay" onClick={() => setFullscreenMedia(null)}>
          <button className="fullscreen-close" onClick={() => setFullscreenMedia(null)}>
            &times;
          </button>
          {fullscreenMedia.type === 'video' ? (
            <video
              src={fullscreenMedia.url}
              className="fullscreen-video"
              controls
              autoPlay
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={fullscreenMedia.url}
              alt="Full size"
              className="fullscreen-image"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </>
  );
};

export default PostCard;
